import React from 'react';
import Sparkline from './Sparkline';

/**
 * KpiCard
 * Muted-palette stat tile with optional delta chip and sparkline.
 *
 * Props:
 *   label: string
 *   value: string | number (pre-formatted)
 *   delta: { text, direction: 'up'|'down'|'flat', pct } (from formatDelta)
 *   deltaTooltip: string — hover tooltip explaining the comparison period
 *   sparklineData: number[]
 *   sparklineColor: string
 *   subtext: string
 *   tone: 'neutral'|'positive'|'negative'|'warning'|'info' (default 'neutral')
 *   compact: boolean — smaller padding / no sparkline (mobile mini row)
 */
export default function KpiCard({
  label,
  value,
  delta,
  deltaTooltip,
  sparklineData,
  sparklineColor = '#6b7280',
  subtext,
  tone = 'neutral',
  compact = false
}) {
  const padding = compact ? 'p-3' : 'p-5';
  const valueSize = compact ? 'text-lg' : 'text-2xl';
  const labelSize = compact ? 'text-xs' : 'text-sm';

  return (
    <div className={`bg-white rounded-lg shadow ${padding} border border-gray-200`}>
      <div className="flex items-center justify-between gap-2">
        <div className={`${labelSize} font-medium text-gray-500`}>{label}</div>
        {delta && <DeltaChip delta={delta} tone={tone} title={deltaTooltip} />}
      </div>
      <div className={`mt-2 ${valueSize} font-bold tabular-nums text-gray-900`}>{value}</div>
      {subtext && <div className="mt-1 text-xs text-gray-400 break-words">{subtext}</div>}
      {!compact && sparklineData && sparklineData.length >= 2 && (
        <div className="mt-3">
          <Sparkline data={sparklineData} color={sparklineColor} height={36} ariaLabel={`${label} trend`} />
        </div>
      )}
    </div>
  );
}

export function DeltaChip({ delta, tone, title }) {
  if (!delta || delta.pct === null) {
    return (
      <span
        className="text-xs text-gray-400"
        title={title}
      >
        —
      </span>
    );
  }

  const { direction, text } = delta;
  // Default: up=green, down=red. Invert for "negative" tone (e.g. deductions — more is bad).
  const isGood = tone === 'negative' ? direction === 'down' : direction === 'up';
  const colorClass = direction === 'flat'
    ? 'bg-gray-100 text-gray-600'
    : isGood
      ? 'bg-green-50 text-green-700'
      : 'bg-red-50 text-red-700';

  return (
    <span
      className={`text-xs font-medium px-1.5 py-0.5 rounded tabular-nums ${colorClass}`}
      title={title}
    >
      {text}
    </span>
  );
}
